// Central default UI wording. Every static string has a unique key tied to where
// it appears. Super Admin edit-mode overrides these via /api/content (stored in DB).
// New screens (Gardens and beyond) add their keys here and render them with <Txt>.
export const STRINGS = {
  // Events · Admin
  "events.admin.title": "Events",
  "events.admin.subtitle": "Create events and publish them to the members' calendar. Drafts stay hidden until you publish.",
  "events.admin.new": "New event",
  "events.admin.empty": "No events yet.",
  // Events · Member calendar
  "events.calendar.title": "Events",
  "events.calendar.subtitle": "What's coming up at Rosemoor.",
  "events.calendar.empty": "No upcoming events right now. Check back soon.",
  "events.rsvp.going": "I'm going",
  "events.rsvp.cant": "Can't make it",
  "events.rsvp.directions": "Get directions",
  "events.rsvp.join": "Join the video call",

  // Membership · Directory
  "membership.directory.title": "Membership directory",
  "membership.directory.add": "Add member",
  "membership.directory.search_placeholder": "Search name, email, #…",
  "membership.directory.all_types": "All types",
  "membership.directory.all_statuses": "All statuses",
  "membership.directory.all_tags": "All tags",
  "membership.directory.prev": "Previous",
  "membership.directory.next": "Next",
  "membership.directory.empty": "No members match.",

  // Membership · Codes
  "membership.codes.title": "Membership codes",
  "membership.codes.subtitle": "Full-waiver or percentage-discount codes. Each is single-use by anyone who enters it.",
  "membership.codes.generate_heading": "Generate a code",
  "membership.codes.generate_btn": "Generate",
  "membership.codes.type_label": "Type",
  "membership.codes.reason_label": "Reason",
  "membership.codes.empty": "No codes yet.",

  // Membership · Dues settings
  "membership.dues.title": "Dues & billing",
  "membership.dues.subtitle": "Set dues per account type. Turning a billing option off removes it from every paid type on Join Rosemoor.",
  "membership.dues.monthly": "Monthly billing",
  "membership.dues.yearly": "Yearly billing",
  "membership.dues.amounts_heading": "Amounts per type",
  "membership.dues.save": "Save dues settings",
  "membership.dues.reasons_heading": "Waived reasons",

  // Member dashboard
  "member.dashboard.card_label": "Membership card",
  "member.dashboard.pin_heading": "Your check-in PIN",
  "member.dashboard.pin_change": "Change PIN",

  // Two-factor
  "twofa.enroll.title": "Set up two-factor",
  "twofa.verify.title": "Two-factor authentication",
  "twofa.enroll.instructions": "Your role has access to sensitive data, so a second step is required. Scan this with Google Authenticator, Authy, or any authenticator app, then enter the 6-digit code.",
  "twofa.verify.instructions": "Enter the 6-digit code from your authenticator app, or one of your backup codes.",
  "twofa.enroll.submit": "Verify & finish",
  "twofa.verify.submit": "Verify",
  "twofa.choose.title": "Set up two-factor",
  "twofa.choose.subtitle": "Your role has access to sensitive data, so a second step is required. Choose how you'd like to verify.",
  "twofa.choose.passkey": "Use a passkey",
  "twofa.choose.passkey_desc": "Face ID, Touch ID, Windows Hello, your phone, or a security key.",
  "twofa.choose.totp": "Use an authenticator app",
  "twofa.choose.totp_desc": "Google Authenticator, Authy, 1Password, or similar.",
  "twofa.verify.passkey_btn": "Verify with your passkey",
  "twofa.verify.use_code": "Use an authenticator or backup code instead",
  "twofa.backup.title": "Save your backup codes",
  "twofa.backup.subtitle": "Each code works once if you ever lose your authenticator. Store them somewhere safe — this is the only time they're shown.",
  "twofa.backup.continue": "I've saved them — continue",

  // Common
  "common.save": "Save",
  "common.cancel": "Cancel",

  // Dashboards
  "dash.super.subtitle": "Here is what needs you today.",
  "dash.super.stat.signups": "New signups this week",
  "dash.super.stat.fulfillment": "Fulfillment backlog",
  "dash.super.stat.grants": "Grant deadlines ≤30 days",
  "dash.super.stat.board": "Board items open",
  "dash.super.stat.dues": "Dues collected, this month",
  "dash.super.events_title": "Upcoming events & RSVPs",
  "dash.super.events_empty": "Events and RSVP counts land here once the Events module ships.",
  "dash.super.attention_title": "Needs your attention",
  "dash.super.attention_empty": "Fulfillment, onboarding, and follow-up alerts will surface here.",
  "dash.super.grant_label": "Grant-ready figure",
  "dash.super.grant_note": "Unduplicated persons served will be drawn from real check-in records, not an estimate.",
  "dash.chair.subtitle": "Membership at a glance.",
  "dash.chair.stat.signups": "New signups this week",
  "dash.chair.stat.renewals": "Renewals due",
  "dash.chair.stat.pending": "Pending payments",
  "dash.chair.stat.lapsed": "Lapsed this quarter",
  "dash.chair.signups_title": "New signups",
  "dash.chair.signups_empty": "New member signups will list here with type and neighborhood tags.",
  "dash.chair.renewals_title": "Renewals due",
  "dash.chair.renewals_empty": "Members with dues coming due appear here for a one-tap renewal segment.",
  "dash.generic.sections_title": "Your sections",
  "dash.generic.empty": "No sections assigned yet.",

  // Connections
  "connections.title": "Connections",
  "connections.subtitle": "Connect the outside services Rosemoor relies on — once, here.",
  "connections.banner": "Enter each service's keys once. Nothing processes payments or sends email yet — that arrives with the modules that use them. Secrets are stored securely and never shown in full again.",
  "connections.status_connected": "Connected",
  "connections.status_not_connected": "Not connected",
  "connections.secret_note": "Stored securely — shown masked once saved.",

  // Modules
  "modules.title": "Modules",
  "modules.subtitle": "Switch whole features on or off, system-wide.",
  "modules.banner": "Switching a module off hides it from every navigation and screen for every role, keeps its data, and brings it back unchanged when re-enabled. Donations is the first live module.",
  "modules.empty": "No modules registered yet. Features register here as they ship.",

  // Trash
  "trash.title": "Trash",
  "trash.subtitle_pre": "Deleted records land here and stay restorable for",
  "trash.subtitle_post": ", then purge automatically. As modules ship, anything you bulk-delete will appear here first.",
  "trash.empty": "Trash is empty. Nothing has been deleted yet.",
  "trash.restore": "Restore",
  "trash.delete_forever": "Delete forever",

  // Reset data
  "reset.title": "Reset system data",
  "reset.subtitle": "Wipe records by type before go-live. This is meant for clearing out test data generated during the build. It permanently removes the selected records — it does not use Trash.",
  "reset.warning": "This cannot be undone. Records removed here are gone for good.",
  "reset.confirm_btn": "Permanently wipe selected data",
  "reset.deleteall.title": "Delete all data",
  "reset.deleteall.tagline": "The most absolute wipe. Separate from everything above.",
  "reset.deleteall.btn": "Delete all data & reset numbering",

  // Roles & access
  "roles.title": "Roles & access",
  "roles.subtitle": "Who can reach what, and the trail of every change.",
  "roles.tab.super": "Super Admins",
  "roles.tab.roles": "Saved roles",
  "roles.tab.people": "People",
  "roles.tab.exceptions": "Per-person exceptions",
  "roles.super.title": "Super Admins",
  "roles.super.desc": "No limit on how many. At least two is wise so the association is never locked out.",
  "roles.audit.title": "Audit log",
  "roles.audit.desc": "Every access change — who, to whom, and when.",
  "roles.saved.title": "Saved roles",
  "roles.saved.desc": "Each is a reusable set of permissions. The next person in a seat inherits it automatically.",
  "roles.saved.create": "Create a role",
  "roles.people.title": "People",
  "roles.people.desc": "Assign one or more roles to each account. Access is the broadest of everything assigned.",
  "roles.exceptions.title": "One person, one exception",
  "roles.exceptions.desc": "Grant a single person access beyond their role, without changing that role for anyone else.",
  "roles.exceptions.grant_btn": "Grant exception",
  "roles.exceptions.active_title": "Active exceptions",
  "roles.editor.section_access": "Section access",
  "roles.editor.section_access_desc": "Set each section independently. Sections set to None disappear from this role's navigation.",

  // Navigation chrome (shared by admin sidebar + member app bottom nav)
  "nav.brand.tagline": "Admin & CRM",
  "nav.search_placeholder": "Search members, visitors, events, grants, partners…",
  "nav.app.home": "Home",

  // Garden Plots
  "gardens.plots.title": "Garden Plots",
  "gardens.plots.add": "Add plot",
  "gardens.plots.subtitle": "Every 100th Street plot — theme, managers, current photo, and outstanding needs at a glance.",
  "gardens.plots.photo_replace": "Upload a new photo",
  "gardens.plots.docs_title": "Documents",
  "gardens.plots.docs_upload": "Upload a document",
  "gardens.plots.drive_connect": "Connect Google Drive",
  "gardens.plots.drive_banner": "Photo and document uploads use the association's shared Google Drive. A Super Admin needs to connect it once.",
};
